import type { PaAPI } from '@rialtic/types'
import {
  ClaimTypeHiro,
  type DropDownItem,
  type RefDataModel,
  type RipProfessionalClaimHeader,
  type RipProfessionalClaimLine,
  type RipInstitutionalClaimHeader,
  type RipInstitutionalClaimLine,
  type RipInstitutionalClaim,
} from '@rialtic/types'
import { h, type Component, type VNode } from 'vue'
import type { FieldValue } from '../composables/fields'
import { useIsRialticUser } from '../composables/user'
import { format, isValid, parse } from 'date-fns'

/* Only the `values` bag of the form context is read when rendering collapsed rows. */
export interface FormContext {
  values: Record<string, FieldValue>
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const formatCollapsedDate = (value: FieldValue) => {
  const date = new Date(String(value))

  return isValid(date) ? format(date, 'MM/dd/yyyy') : '-'
}

/* The native attributes the editor passes to every widget input */
export interface WidgetAttributes {
  class?: string
  id?: string
  autocomplete?: string
  placeholder?: string
  required?: boolean
  readonly?: boolean
  disabled?: boolean
  [datasetKey: `data-${string}`]: string | boolean | undefined
}

export enum ComponentType {
  AUTOCOMPLETE = 'autocomplete',
  TEXT = 'text',
  DATE = 'date',
  CODES = 'codes',
  LIST = 'list',
  CURRENCY = 'currency',
}

export interface RowSchemaMeta {
  class?: string
  name?: string
  label: string
  dataType?: 'number' | 'string' | 'boolean'
  isRequired: boolean | ((claimType: ClaimTypeHiro) => boolean)
  isReadonly?: boolean | ((claimType: ClaimTypeHiro) => boolean)
  isEditable: boolean
  isHidden?: boolean | ((connector?: PaAPI.Schemas['Connector']) => boolean)
  items?: DropDownItem[]
  modelValue?: string | number
  placeholder?: string
  refDataModel?: RefDataModel
  editKey?: string
  component?: ComponentType | Component
  tupleSize?: number
  maxEntries?: number
  expandItems?: RowSchema[] | ((claim: RipInstitutionalClaim) => RowSchema[])
  // Returns a render function
  renderCollapsedValue?: (
    rows: RowSchema[],
    formContext: FormContext,
  ) => () => VNode
  getTemplateItem?: (index: number) => RowSchema | RowSchema[]
  buttonLabels?: {
    add: string
    edit: string
    collapse: string
  }
}

export interface RowSchema extends RowSchemaMeta {
  name: string
  isReadonly?: boolean
  isRequired: boolean
  component?: ComponentType | Component
  expandItems?: RowSchema[]
  disabled?: boolean
}

export interface ClaimFieldsSchema {
  line: RowSchema[]
  header: RowSchema[]
  internal: RowSchema[]
}

export interface RemoveFieldsPayload {
  metaField: string
  source: 'header' | 'internal'
  keys: string[]
}

type ClaimLineUiField = 'existing_status' | 'test_status' | 'bypass_code'

type ProfessionalClaimLineField =
  | keyof RipProfessionalClaimLine
  | ClaimLineUiField

type ProfessionalClaimHeaderField = keyof RipProfessionalClaimHeader

type ProfessionalClaimInternalField = 'patient_birthdate'

interface ProfessionalClaimFieldsLookup {
  line: ProfessionalClaimLineField[]
  header: ProfessionalClaimHeaderField[]
  internal: ProfessionalClaimInternalField[]
}

type InstitutionalClaimLineField =
  | keyof RipInstitutionalClaimLine
  | ClaimLineUiField

export type InstitutionalClaimHeaderField =
  | keyof Omit<
      RipInstitutionalClaimHeader,
      'patient_birthdate' | 'patient_discharge_status'
    >
  | 'diagnoses'

type InstitutionalClaimInternalField =
  | keyof Pick<
      RipInstitutionalClaimHeader,
      'patient_birthdate' | 'patient_discharge_status'
    >
  | 'value_codes'
  | 'occurrence_codes'
  | 'condition_codes'

interface InstitutionalClaimFieldsLookup {
  line: InstitutionalClaimLineField[]
  header: InstitutionalClaimHeaderField[]
  internal: InstitutionalClaimInternalField[]
}

export const professionalFields: ProfessionalClaimFieldsLookup = {
  line: [
    'line_number',
    'existing_status',
    'test_status',
    'date_of_service_from',
    'date_of_service_to',
    'procedure_code',
    'ndc_code',
    'place_of_service',
    'units',
    'modifier_1',
    'modifier_2',
    'modifier_3',
    'modifier_4',
    'diagnosis_code_1',
    'diagnosis_code_2',
    'diagnosis_code_3',
    'diagnosis_code_4',
    'rendering_provider_npi',
    'rendering_provider_taxonomy',
    'bypass_code',
  ],
  header: [
    'billing_provider_tin',
    'billing_provider_npi',
    'billing_provider_taxonomy',
    'mcd_provider_type',
    'provider_specialty_1',
    'provider_specialty_2',
    'provider_specialty_3',
    'provider_specialty_4',
  ],
  internal: ['patient_birthdate'],
}

export const institutionalFields: InstitutionalClaimFieldsLookup = {
  line: [
    'line_number',
    'existing_status',
    'test_status',
    'date_of_service_from',
    'date_of_service_to',
    'revenue_code',
    'procedure_code',
    'ndc_code',
    'units',
    'modifier_1',
    'modifier_2',
    'modifier_3',
    'modifier_4',
    'rendering_provider_npi',
    'rendering_provider_taxonomy',
    'bypass_code',
  ],
  header: [
    'type_of_bill',
    'billing_provider_tin',
    'billing_provider_npi',
    'billing_provider_taxonomy',
    'mcd_provider_type',
    'provider_specialty_1',
    'provider_specialty_2',
    'provider_specialty_3',
    'provider_specialty_4',
    'diagnoses',
    'admitting_diagnosis_code',
  ],
  internal: [
    'patient_birthdate',
    'value_codes',
    'occurrence_codes',
    'condition_codes',
    'patient_discharge_status',
  ],
}

export const claimFields: Partial<
  Record<
    | ProfessionalClaimLineField
    | ProfessionalClaimHeaderField
    | ProfessionalClaimInternalField
    | InstitutionalClaimLineField
    | InstitutionalClaimHeaderField
    | InstitutionalClaimInternalField,
    RowSchemaMeta
  >
> = {
  line_number: { label: 'Line', isRequired: false, isEditable: false },
  existing_status: {
    label: 'Existing Status',
    isRequired: false,
    isEditable: false,
  },
  test_status: { label: 'Test Status', isRequired: false, isEditable: false },
  date_of_service_from: {
    label: 'Date of Service (From)',
    isRequired: true,
    isEditable: true,
    editKey: 'date_of_service_from',
    component: ComponentType.DATE,
  },
  date_of_service_to: {
    label: 'Date of Service (To)',
    isRequired: true,
    isEditable: true,
    editKey: 'date_of_service_to',
    component: ComponentType.DATE,
  },
  procedure_code: {
    label: 'CPT®/HCPCS',
    isRequired: (claimType: ClaimTypeHiro) =>
      [ClaimTypeHiro.Professional, ClaimTypeHiro.ASCProfessional].includes(
        claimType,
      ),
    isEditable: true,
    editKey: 'procedure_code',
    refDataModel: 'procedure',
    component: ComponentType.AUTOCOMPLETE,
  },
  ndc_code: {
    label: 'NDC Code',
    isRequired: false,
    isEditable: true,
    editKey: 'ndc_code',
    component: ComponentType.TEXT,
  },
  place_of_service: {
    label: 'Place of Service',
    isRequired: true,
    isEditable: true,
    isReadonly: (claimType: ClaimTypeHiro) =>
      claimType === ClaimTypeHiro.ASCProfessional,
    editKey: 'place_of_service',
    refDataModel: 'PlaceOfService',
    component: ComponentType.AUTOCOMPLETE,
  },
  units: {
    label: 'Units',
    dataType: 'number',
    isRequired: true,
    isEditable: true,
    editKey: 'units',
    component: ComponentType.TEXT,
  },
  modifier_1: {
    label: 'Modifier 1',
    isRequired: false,
    isEditable: true,
    editKey: 'modifier_1',
    refDataModel: 'modifier',
    component: ComponentType.AUTOCOMPLETE,
  },
  modifier_2: {
    label: 'Modifier 2',
    isRequired: false,
    isEditable: true,
    editKey: 'modifier_2',
    refDataModel: 'modifier',
    component: ComponentType.AUTOCOMPLETE,
  },
  modifier_3: {
    label: 'Modifier 3',
    isRequired: false,
    isEditable: true,
    editKey: 'modifier_3',
    refDataModel: 'modifier',
    component: ComponentType.AUTOCOMPLETE,
  },
  modifier_4: {
    label: 'Modifier 4',
    isRequired: false,
    isEditable: true,
    editKey: 'modifier_4',
    refDataModel: 'modifier',
    component: ComponentType.AUTOCOMPLETE,
  },
  diagnosis_code_1: {
    label: 'Diagnosis 1',
    isRequired: true,
    isEditable: true,
    editKey: 'diagnosis_code_1',
    refDataModel: 'diagnosis',
    component: ComponentType.AUTOCOMPLETE,
  },
  diagnosis_code_2: {
    label: 'Diagnosis 2',
    isRequired: false,
    isEditable: true,
    editKey: 'diagnosis_code_2',
    refDataModel: 'diagnosis',
    component: ComponentType.AUTOCOMPLETE,
  },
  diagnosis_code_3: {
    label: 'Diagnosis 3',
    isRequired: false,
    isEditable: true,
    editKey: 'diagnosis_code_3',
    refDataModel: 'diagnosis',
    component: ComponentType.AUTOCOMPLETE,
  },
  diagnosis_code_4: {
    label: 'Diagnosis 4',
    isRequired: false,
    isEditable: true,
    editKey: 'diagnosis_code_4',
    refDataModel: 'diagnosis',
    component: ComponentType.AUTOCOMPLETE,
  },
  diagnoses: {
    label: 'Diagnoses',
    isRequired: true,
    isEditable: true,
    editKey: 'diagnoses',
    refDataModel: 'diagnosis',
    component: ComponentType.LIST,
    maxEntries: 25,
  },
  provider_specialty_1: {
    label: 'Provider Specialty 1',
    isRequired: false,
    isEditable: true,
    editKey: 'provider_specialty_1',
    refDataModel: 'specialty',
    component: ComponentType.TEXT,
  },
  provider_specialty_2: {
    label: 'Provider Specialty 2',
    isRequired: false,
    isEditable: true,
    editKey: 'provider_specialty_2',
    refDataModel: 'specialty',
    component: ComponentType.TEXT,
  },
  provider_specialty_3: {
    label: 'Provider Specialty 3',
    isRequired: false,
    isEditable: true,
    editKey: 'provider_specialty_3',
    refDataModel: 'specialty',
    component: ComponentType.TEXT,
  },
  provider_specialty_4: {
    label: 'Provider Specialty 4',
    isRequired: false,
    isEditable: true,
    editKey: 'provider_specialty_4',
    refDataModel: 'specialty',
    component: ComponentType.TEXT,
  },
  rendering_provider_npi: {
    label: 'Rendering Provider NPI',
    isRequired: false,
    isEditable: true,
    editKey: 'rendering_provider_npi',
    component: ComponentType.TEXT,
  },
  rendering_provider_taxonomy: {
    label: 'Rendering Prov. Taxonomy',
    isRequired: false,
    isEditable: true,
    editKey: 'rendering_provider_taxonomy',
    component: ComponentType.TEXT,
  },
  bypass_code: {
    label: 'Bypass Code',
    isRequired: false,
    isEditable: false,
  },
  billing_provider_taxonomy: {
    label: 'Billing Provider Taxonomy',
    isRequired: false,
    isEditable: true,
    editKey: 'billing_provider_taxonomy',
    component: ComponentType.TEXT,
  },
  billing_provider_npi: {
    label: 'Billing Provider NPI',
    isRequired: false,
    isEditable: true,
    editKey: 'billing_provider_npi',
    component: ComponentType.TEXT,
  },
  mcd_provider_type: {
    label: 'Medicaid Provider Type',
    isRequired: false,
    isEditable: true,
    isHidden: (connector) =>
      !connector?.system_document?.claim_lab_mcd_provider_type,
    editKey: 'mcd_provider_type',
    component: ComponentType.TEXT,
  },
  type_of_bill: {
    label: 'Type of Bill',
    isRequired: true,
    isEditable: true,
    isReadonly: (claimType: ClaimTypeHiro) =>
      claimType === ClaimTypeHiro.ASCInstitutional,
    editKey: 'type_of_bill',
    component: ComponentType.AUTOCOMPLETE,
    refDataModel: 'TypeOfBill',
  },
  admitting_diagnosis_code: {
    label: 'Admitting Diagnosis',
    isRequired: false,
    isEditable: true,
    editKey: 'admitting_diagnosis_code',
    component: ComponentType.AUTOCOMPLETE,
    refDataModel: 'Diagnosis',
  },
  revenue_code: {
    label: 'Revenue Code',
    isRequired: false,
    isEditable: true,
    editKey: 'revenue_code',
    refDataModel: 'revenue',
    component: ComponentType.AUTOCOMPLETE,
  },
  billing_provider_tin: {
    label: 'Billing Provider TIN',
    isRequired: true,
    isEditable: true,
    editKey: 'billing_provider_tin',
    component: ComponentType.TEXT,
  },
  patient_discharge_status: {
    label: 'Patient Discharge Status',
    isRequired: false,
    isEditable: true,
    editKey: 'patient_discharge_status',
    component: ComponentType.AUTOCOMPLETE,
    refDataModel: 'patientdischargestatuscode',
  },
  patient_birthdate: {
    label: 'Patient Birthdate',
    isRequired: false,
    isEditable: true,
    editKey: 'patient_birthdate',
    component: ComponentType.DATE,
  },
  value_codes: {
    label: 'Value Codes & Amounts',
    isRequired: false,
    isEditable: true,
    component: ComponentType.CODES,
    maxEntries: 24,
    tupleSize: 2,
    buttonLabels: {
      add: 'Add New Code',
      edit: 'Edit Codes & Amounts',
      collapse: 'Collapse Codes & Amounts',
    },
    renderCollapsedValue: (rows, formContext) => () => {
      if (!rows.length) return h('span')

      const children: VNode[] = []

      for (let i = 0; i < rows.length; i += 2) {
        const [code, amount] = rows.slice(i, i + 2)

        if (!code || !amount) continue

        const codeValue = formContext.values[code.editKey!]
        const amountValue = formContext.values[amount.editKey!]

        children.push(
          h('span', { class: 'inline-flex gap-1' }, [
            h('span', { class: 'font-semibold' }, String(codeValue ?? '')),
            h(
              'span',
              /* v8 ignore next */
              `(${amountValue ? usdFormatter.format(Number(amountValue)) : '-'})${i < 46 ? ';' : ''}`,
            ),
          ]),
        )
      }

      return h('span', { class: 'inline-flex gap-1 flex-wrap' }, children)
    },
    getTemplateItem: (index): [RowSchema, RowSchema] => {
      return [
        {
          label: `Value Code ${index + 1}`,
          name: `value_code_${index + 1}`,
          class: 'editable-cell',
          dataType: 'string',
          isRequired: false,
          isEditable: true,
          editKey: `value_code_${index + 1}`,
          refDataModel: 'valuecode',
          placeholder: 'Select',
          modelValue: undefined,
          component: ComponentType.AUTOCOMPLETE,
        },
        {
          label: `Value Code Amount ${index + 1}`,
          name: `value_code_amount_${index + 1}`,
          class: 'editable-cell',
          dataType: 'string',
          isRequired: false,
          isEditable: true,
          editKey: `value_code_amount_${index + 1}`,
          placeholder: 'Type here',
          modelValue: undefined,
          component: ComponentType.CURRENCY,
        },
      ]
    },
    expandItems(claim): RowSchema[] {
      const length = Object.keys(claim.header).reduce((count, fieldName) => {
        return /^value_code_\d{1,2}$/.test(fieldName) ? count + 1 : count
      }, 0)

      return Array.from({ length }, (_, i) => {
        const tuple = this.getTemplateItem!(i) as [RowSchema, RowSchema]

        tuple[0].modelValue =
          claim.header[<keyof RipInstitutionalClaimHeader>`value_code_${i + 1}`]

        let amountValue = claim.header[
          <keyof RipInstitutionalClaimHeader>`value_code_amount_${i + 1}`
        ] as string

        if (amountValue) {
          const [dollars, cents = ''] = String(amountValue)?.split('.')

          // value code amounts come back as strings and may have only 1 decimal or no decimal
          // backfill cents up to 2 decimal places to prevent formatting triggering edit state
          amountValue = `${dollars}.${cents.padEnd(2, '0')}`
        }

        tuple[1].modelValue = amountValue

        return tuple
      }).flat()
    },
  },
  occurrence_codes: {
    label: 'Occurrence Codes & Dates',
    isRequired: false,
    isEditable: true,
    component: ComponentType.CODES,
    maxEntries: 24,
    tupleSize: 2,
    buttonLabels: {
      add: 'Add New Code',
      edit: 'Edit Codes & Dates',
      collapse: 'Collapse Codes & Dates',
    },
    renderCollapsedValue: (rows, formContext) => () => {
      if (!rows.length) return h('span')

      const children: VNode[] = []

      for (let i = 0; i < rows.length; i += 2) {
        const [code, date] = rows.slice(i, i + 2)

        if (!code || !date) continue

        const codeValue = formContext.values[code.editKey!]
        const dateValue = formContext.values[date.editKey!]

        children.push(
          h('span', { class: 'inline-flex gap-1' }, [
            h('span', { class: 'font-semibold' }, String(codeValue ?? '')),
            h(
              'span',
              /* v8 ignore next */
              `(${dateValue ? formatCollapsedDate(dateValue) : '-'}) ${i < 46 ? ';' : ''}`,
            ),
          ]),
        )
      }

      return h('span', { class: 'inline-flex gap-1 flex-wrap' }, children)
    },
    getTemplateItem: (index): [RowSchema, RowSchema] => {
      return [
        {
          label: `Occurrence Code ${index + 1}`,
          name: `occurrence_code_${index + 1}`,
          class: 'editable-cell',
          isRequired: false,
          isEditable: true,
          editKey: `occurrence_code_${index + 1}`,
          refDataModel: 'occurrencecode',
          modelValue: undefined,
          component: ComponentType.AUTOCOMPLETE,
        },
        {
          label: `Occurrence Date ${index + 1}`,
          name: `occurrence_code_date_${index + 1}`,
          class: 'editable-cell',
          isRequired: false,
          isEditable: true,
          editKey: `occurrence_code_date_${index + 1}`,
          modelValue: undefined,
          component: ComponentType.DATE,
        },
      ]
    },
    expandItems(claim): RowSchema[] {
      const length = Object.keys(claim.header).reduce((count, fieldName) => {
        return /^occurrence_code_\d{1,2}$/.test(fieldName) ? count + 1 : count
      }, 0)

      return Array.from({ length }, (_, i) => {
        const tuple = this.getTemplateItem!(i) as [RowSchema, RowSchema]

        tuple[0].modelValue =
          claim.header[
            <keyof RipInstitutionalClaimHeader>`occurrence_code_${i + 1}`
          ]

        let dateValue =
          claim.header[
            // TODO: occurrence code date fields are defined in the ERS as `occurrence_date_{number}` but are being returned as `occurrence_code_date_{number}`. this may change later
            <keyof RipInstitutionalClaimHeader>`occurrence_code_date_${i + 1}`
          ]

        // Hiro is returning ISO8601 basic dates which aren't usable in JS. If we get back this format, re-format to add hyphens
        if (dateValue && /^\d{8}$/.test(String(dateValue))) {
          dateValue = format(
            parse(dateValue as string, 'yyyyMMdd', new Date()),
            'yyyy-MM-dd',
          )
        }

        tuple[1].modelValue = dateValue

        return tuple
      }).flat()
    },
  },
  condition_codes: {
    label: 'Condition Codes',
    isRequired: false,
    isEditable: true,
    component: ComponentType.CODES,
    maxEntries: 24,
    tupleSize: 1,
    buttonLabels: {
      add: 'Add New Code',
      edit: 'Edit Codes',
      collapse: 'Collapse Codes',
    },
    renderCollapsedValue: (rows, formContext) => () => {
      if (!rows.length) return h('span')

      const children: VNode[] = []

      let i = 0

      for (const row of rows) {
        const codeValue = formContext.values[row.editKey!]

        children.push(
          h(
            'span',
            /* v8 ignore next */
            `${codeValue || '-'}${rows.length > 1 && i < rows.length - 1 ? ', ' : ''}`,
          ),
        )
        i++
      }

      return h('span', { class: 'inline-flex gap-1 flex-wrap' }, children)
    },
    getTemplateItem: (index): RowSchema => {
      return {
        label: `Condition Code ${index + 1}`,
        name: `condition_code_${index + 1}`,
        class: 'editable-cell',
        isRequired: false,
        isEditable: true,
        editKey: `condition_code_${index + 1}`,
        refDataModel: 'conditioncode',
        modelValue: undefined,
        component: ComponentType.AUTOCOMPLETE,
      }
    },
    expandItems(claim): RowSchema[] {
      const length = Object.keys(claim.header).reduce((count, fieldName) => {
        return fieldName.startsWith('condition_code') ? count + 1 : count
      }, 0)

      return Array.from({ length }, (_, i) => {
        const item = this.getTemplateItem!(i) as RowSchema

        item.modelValue =
          claim.header[
            <keyof RipInstitutionalClaimHeader>`condition_code_${i + 1}`
          ]

        return item
      })
    },
  },
}

export const getClaimFields = (claimType: ClaimTypeHiro): ClaimFieldsSchema => {
  const isRialticUser = useIsRialticUser()

  const fields = claimType.includes('professional')
    ? professionalFields
    : institutionalFields

  const mapFields = (
    key:
      | ProfessionalClaimLineField
      | ProfessionalClaimHeaderField
      | ProfessionalClaimInternalField
      | InstitutionalClaimLineField
      | InstitutionalClaimHeaderField
      | InstitutionalClaimInternalField,
  ): RowSchema => {
    const field = { ...claimFields[key] } as RowSchemaMeta

    field.name = key as string

    field.placeholder = [
      ComponentType.AUTOCOMPLETE,
      ComponentType.TEXT,
      ComponentType.LIST,
      ComponentType.CURRENCY,
    ].includes(field.component as ComponentType)
      ? 'Type here'
      : 'Select'

    if (typeof field.isReadonly === 'function') {
      field.isReadonly = field.isReadonly(claimType)
    }

    if (typeof field.isRequired === 'function') {
      field.isRequired = field.isRequired(claimType)
    }

    if (
      !isRialticUser.value &&
      (professionalFields.internal.includes(
        key as ProfessionalClaimInternalField,
      ) ||
        institutionalFields.internal.includes(
          key as InstitutionalClaimInternalField,
        ))
    ) {
      field.isEditable = false
    }

    return field as RowSchema
  }

  const line = fields.line.map(mapFields)
  const header = fields.header.map(mapFields)
  const internal = fields.internal.map(mapFields)

  return { line, header, internal }
}
